import React from 'react';
import { Form, Input, InputNumber, Select, Checkbox, DatePicker } from 'antd';
import type { CustomFieldConfig } from '../services/missionTypeService';

interface CustomFieldRendererProps {
  fields: CustomFieldConfig[];
}

/**
 * Renders Ant Design form items for each custom field defined on a mission type.
 * Form.Item names are nested as ['mission_data', field.name] so values collect under mission_data.
 */
const CustomFieldRenderer: React.FC<CustomFieldRendererProps> = ({ fields }) => {
  if (!fields || fields.length === 0) return null;

  return (
    <>
      {fields.map((field) => {
        const rules: any[] = [];
        if (field.required) {
          rules.push({ required: true, message: `${field.label} est requis` });
        }
        if (field.type === 'number') {
          rules.push({
            type: 'number' as const,
            message: `${field.label} doit être un nombre`,
          });
          if (field.validation?.min !== undefined) {
            rules.push({
              type: 'number' as const,
              min: field.validation.min,
              message: `${field.label} doit être ≥ ${field.validation.min}`,
            });
          }
          if (field.validation?.max !== undefined) {
            rules.push({
              type: 'number' as const,
              max: field.validation.max,
              message: `${field.label} doit être ≤ ${field.validation.max}`,
            });
          }
        }

        const valuePropName = field.type === 'checkbox' ? 'checked' : 'value';

        return (
          <Form.Item
            key={field.name}
            name={['mission_data', field.name]}
            label={field.label}
            rules={rules}
            valuePropName={valuePropName}
          >
            {renderFieldInput(field)}
          </Form.Item>
        );
      })}
    </>
  );
};

function renderFieldInput(field: CustomFieldConfig): React.ReactNode {
  switch (field.type) {
    case 'text':
      return <Input placeholder={field.placeholder} />;

    case 'number':
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder={field.placeholder}
          min={field.validation?.min}
          max={field.validation?.max}
        />
      );

    case 'select':
      return (
        <Select placeholder={field.placeholder || 'Sélectionner...'}>
          {(field.options || []).map((opt) => (
            <Select.Option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
      );

    case 'checkbox':
      return <Checkbox>{field.label}</Checkbox>;

    case 'textarea':
      return <Input.TextArea placeholder={field.placeholder} rows={3} />;

    case 'date':
      return (
        <DatePicker
          style={{ width: '100%' }}
          placeholder={field.placeholder || 'Sélectionner une date'}
          format="DD/MM/YYYY"
        />
      );

    default:
      return <Input placeholder={field.placeholder} />;
  }
}

export default CustomFieldRenderer;
